/**
 * Relax Sauna PDP — vanilla JS behaviors.
 *
 * No framework, no build step. Loaded with `defer`, so the DOM is parsed by
 * the time this runs. Every hook is a data-attribute namespaced pdp-relax-*
 * so it can't collide with the host theme's own scripts.
 *
 * Markup contracts this file expects (see the snippets under
 * shopify-export/snippets/ for the actual HTML):
 *
 *   Countdown (urgency banner + sticky header):
 *     [data-pdp-countdown-root]            element to hide entirely once
 *                                           the timer hits 0, and to reveal
 *                                           (adds .is-ready) once JS has
 *                                           computed the real remaining time
 *     [data-pdp-countdown-hours]
 *     [data-pdp-countdown-minutes]
 *     [data-pdp-countdown-seconds]
 *     [data-pdp-coupon-trigger]            "Get Coupon" buttons
 *
 *   Sticky header:
 *     [data-pdp-sticky]                    the fixed bar, toggled .is-visible
 *
 *   Hero gallery:
 *     [data-pdp-hero-gallery]              gallery root
 *     [data-pdp-hero-image]                the large image
 *     [data-pdp-hero-prev] / [data-pdp-hero-next]
 *     [data-pdp-hero-thumb][data-index][data-src][data-alt]
 *     [data-pdp-hero-dot][data-index]
 *
 *   Accordion:
 *     [data-pdp-accordion]                 accordion root
 *     .pdp-relax-accordion-item[data-pdp-accordion-item]
 *     [data-pdp-accordion-trigger]         inside each item
 *
 *   Stat counters:
 *     [data-pdp-stat-target="94"]          element whose text content is
 *                                           the already-correct "94%" and
 *                                           gets animated from 0 on scroll
 *
 *   Financing slider:
 *     [data-pdp-financing][data-total-cents]   module root
 *     [data-pdp-financing-range]               3–12 month <input type=range>
 *     [data-pdp-financing-term="N"]            term-select buttons
 *     [data-pdp-financing-monthly]             computed monthly $ text
 *     [data-pdp-financing-months]              selected term count text
 *
 *   Coupon modal (opened by the "pdp-relax:open-coupon" event dispatched
 *   by any [data-pdp-coupon-trigger] button):
 *     [data-pdp-coupon-modal]              modal root, toggled .is-open
 *     [data-pdp-coupon-close]              close buttons (X + "Continue")
 *     [data-pdp-coupon-form]               the email capture <form>
 *     [data-pdp-coupon-form-wrap]          hidden after successful submit
 *     [data-pdp-coupon-reveal]             shown after successful submit
 *     [data-pdp-coupon-email]              filled with the submitted email
 */
(function () {
  "use strict";

  var root = document.getElementById("pdp-relax-sauna");
  if (!root) return;

  var STORAGE_KEY = "pdp-relax-urgency-timer-end";

  /* ---------------- Countdown ---------------- */

  function getTimerEnd() {
    var hours = parseFloat(root.getAttribute("data-urgency-hours") || "6");
    var stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable (privacy mode, etc.) — fall back to a fresh timer */
    }
    if (stored) return parseInt(stored, 10);
    var end = Date.now() + hours * 60 * 60 * 1000;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(end));
    } catch (e) {
      /* ignore */
    }
    return end;
  }

  function pad(n) {
    return String(n).padStart(2, "0");
  }

  function initCountdowns() {
    var nodes = root.querySelectorAll("[data-pdp-countdown-root]");
    if (!nodes.length) return;
    var end = getTimerEnd();

    function tick() {
      var remaining = Math.max(0, Math.floor((end - Date.now()) / 1000));
      var expired = remaining <= 0;
      var hours = Math.floor(remaining / 3600);
      var minutes = Math.floor((remaining % 3600) / 60);
      var seconds = remaining % 60;

      nodes.forEach(function (node) {
        if (expired) {
          node.style.display = "none";
          return;
        }
        node.classList.add("is-ready");
        var h = node.querySelector("[data-pdp-countdown-hours]");
        var m = node.querySelector("[data-pdp-countdown-minutes]");
        var s = node.querySelector("[data-pdp-countdown-seconds]");
        if (h) h.textContent = pad(hours);
        if (m) m.textContent = pad(minutes);
        if (s) s.textContent = pad(seconds);
      });

      if (expired && interval) clearInterval(interval);
    }

    var interval = setInterval(tick, 1000);
    tick();
  }

  /* ---------------- Sticky header ---------------- */

  function initStickyHeader() {
    var sticky = root.querySelector("[data-pdp-sticky]");
    if (!sticky) return;

    var themeHeader =
      document.querySelector(".shopify-section-header-sticky") ||
      document.querySelector('[id^="shopify-section"][id*="header"]') ||
      document.querySelector("header");

    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      var show = y > 600;
      sticky.classList.toggle("is-visible", show);

      var offset = 0;
      if (themeHeader) {
        offset = Math.max(0, themeHeader.getBoundingClientRect().bottom);
      }
      sticky.style.top = offset + "px";

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
  }

  /* ---------------- Hero gallery ---------------- */

  function initHeroGalleries() {
    root.querySelectorAll("[data-pdp-hero-gallery]").forEach(function (gallery) {
      var image = gallery.querySelector("[data-pdp-hero-image]");
      var thumbs = Array.prototype.slice.call(
        gallery.querySelectorAll("[data-pdp-hero-thumb]")
      );
      var dots = Array.prototype.slice.call(gallery.querySelectorAll("[data-pdp-hero-dot]"));
      var prevBtn = gallery.querySelector("[data-pdp-hero-prev]");
      var nextBtn = gallery.querySelector("[data-pdp-hero-next]");
      if (!image || !thumbs.length) return;

      var count = thumbs.length;
      var current = 0;

      function render() {
        var thumb = thumbs[current];
        image.setAttribute("src", thumb.getAttribute("data-src"));
        image.setAttribute("alt", thumb.getAttribute("data-alt") || "");
        thumbs.forEach(function (t, i) {
          t.classList.toggle("is-active", i === current);
          t.setAttribute("aria-current", i === current ? "true" : "false");
        });
        dots.forEach(function (d, i) {
          d.classList.toggle("is-active", i === current);
          d.setAttribute("aria-current", i === current ? "true" : "false");
        });
      }

      function goTo(index) {
        current = ((index % count) + count) % count;
        render();
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener("click", function () {
          goTo(i);
        });
      });
      dots.forEach(function (dot, i) {
        dot.addEventListener("click", function () {
          goTo(i);
        });
      });
      if (prevBtn) prevBtn.addEventListener("click", function () { goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { goTo(current + 1); });

      render();
    });
  }

  /* ---------------- Accordion ---------------- */

  function initAccordions() {
    root.querySelectorAll("[data-pdp-accordion]").forEach(function (accordion) {
      var items = Array.prototype.slice.call(
        accordion.querySelectorAll("[data-pdp-accordion-item]")
      );
      function setOpen(item, open) {
        item.classList.toggle("is-open", open);
        var trigger = item.querySelector("[data-pdp-accordion-trigger]");
        if (trigger) trigger.setAttribute("aria-expanded", String(open));
      }

      items.forEach(function (item) {
        var trigger = item.querySelector("[data-pdp-accordion-trigger]");
        if (!trigger) return;
        trigger.addEventListener("click", function () {
          var wasOpen = item.classList.contains("is-open");
          items.forEach(function (other) {
            setOpen(other, false);
          });
          if (!wasOpen) setOpen(item, true);
        });
      });
    });
  }

  /* ---------------- Stat counters ----------------
   * The number in the markup is already the real target (correct with JS
   * disabled). With JS, and only once "IntersectionObserver" exists, we
   * reset to 0 and count up when the card scrolls into view. */

  function initStatCounters() {
    if (!("IntersectionObserver" in window)) return;
    var nodes = root.querySelectorAll("[data-pdp-stat-target]");
    if (!nodes.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var node = entry.target;
          var target = parseInt(node.getAttribute("data-pdp-stat-target"), 10) || 0;
          var current = 0;
          function step() {
            current += 1;
            node.textContent = Math.min(current, target) + "%";
            if (current < target) window.requestAnimationFrame(step);
          }
          window.requestAnimationFrame(step);
          observer.unobserve(node);
        });
      },
      { threshold: 0.3 }
    );

    nodes.forEach(function (node) {
      node.textContent = "0%";
      observer.observe(node);
    });
  }

  /* ---------------- Financing slider ---------------- */

  function initFinancingSlider() {
    var container = root.querySelector("[data-pdp-financing]");
    if (!container) return;

    var totalDollars = (parseFloat(container.getAttribute("data-total-cents")) || 0) / 100;
    var range = container.querySelector("[data-pdp-financing-range]");
    var monthlyEl = container.querySelector("[data-pdp-financing-monthly]");
    var monthsEl = container.querySelector("[data-pdp-financing-months]");
    var termButtons = Array.prototype.slice.call(
      container.querySelectorAll("[data-pdp-financing-term]")
    );

    function update(months) {
      var monthly = Math.ceil(totalDollars / months);
      if (monthlyEl) monthlyEl.textContent = monthly;
      if (monthsEl) monthsEl.textContent = months;
      if (range) range.value = months;
      termButtons.forEach(function (btn) {
        var btnMonths = parseInt(btn.getAttribute("data-pdp-financing-term"), 10);
        btn.classList.toggle("is-active", btnMonths === months);
      });
    }

    if (range) {
      range.addEventListener("input", function () {
        update(parseInt(range.value, 10));
      });
    }
    termButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        update(parseInt(btn.getAttribute("data-pdp-financing-term"), 10));
      });
    });
  }

  /* ---------------- Coupon triggers + modal ---------------- */

  function initCouponTriggers() {
    root.querySelectorAll("[data-pdp-coupon-trigger]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        document.dispatchEvent(new CustomEvent("pdp-relax:open-coupon"));
      });
    });
  }

  function initCouponModal() {
    var modal = root.querySelector("[data-pdp-coupon-modal]");
    if (!modal) return;

    var panel = modal.querySelector(".pdp-relax-coupon-modal__panel");
    var form = modal.querySelector("[data-pdp-coupon-form] form");
    var formWrap = modal.querySelector("[data-pdp-coupon-form-wrap]");
    var reveal = modal.querySelector("[data-pdp-coupon-reveal]");
    var lastFocused = null;

    function focusableEls() {
      return Array.prototype.slice.call(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    }

    function openModal() {
      lastFocused = document.activeElement;
      modal.classList.add("is-open");
      document.body.style.overflow = "hidden";
      var els = focusableEls();
      if (els.length) els[0].focus();
    }

    function closeModal() {
      modal.classList.remove("is-open");
      document.body.style.overflow = "";
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    document.addEventListener("pdp-relax:open-coupon", openModal);

    modal.querySelectorAll("[data-pdp-coupon-close]").forEach(function (btn) {
      btn.addEventListener("click", closeModal);
    });

    modal.addEventListener("click", function (e) {
      if (e.target === modal) closeModal();
    });

    document.addEventListener("keydown", function (e) {
      if (!modal.classList.contains("is-open")) return;
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key !== "Tab") return;
      var els = focusableEls();
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    if (form) {
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var emailInput = form.querySelector('input[type="email"]');
        var email = emailInput ? emailInput.value : "";
        if (!email) return;

        // Real submission to Shopify's customer/newsletter endpoint —
        // fire-and-forget so the UI doesn't block on it. This isn't a fake
        // form: the email is genuinely captured in Shopify.
        try {
          fetch(form.action, { method: "POST", body: new FormData(form) }).catch(function () {});
        } catch (err) {
          /* fetch unsupported or blocked — email still shown below, code still revealed */
        }

        var emailOut = modal.querySelector("[data-pdp-coupon-email]");
        if (emailOut) emailOut.textContent = email;
        if (formWrap) formWrap.hidden = true;
        if (reveal) {
          reveal.hidden = false;
          var continueBtn = reveal.querySelector("[data-pdp-coupon-close]");
          if (continueBtn) continueBtn.focus();
        }
      });
    }
  }

  initCountdowns();
  initStickyHeader();
  initHeroGalleries();
  initAccordions();
  initStatCounters();
  initFinancingSlider();
  initCouponTriggers();
  initCouponModal();
})();
